import { useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Page } from "@boundary/layouts/pageLayout";
import { OrderService } from "@domain/services/order.service";
import { prefetchAndNavigate } from "@boundary/utils/prefetchRoute";
import { useDebugEmulations } from "@boundary/components/debugWidget";
import styles from "./paymentStatus.module.scss";

type PaymentStatusType = "success" | "fail" | "timeout" | "receipt_error";

const REDIRECT_DELAY = 4000;

const DEBUG_OPTIONS = [
  { key: "statusSuccess", label: "Успешная оплата" },
  { key: "statusFail", label: "Оплата не прошла" },
  { key: "statusTimeout", label: "Превышено время ожидания" },
  { key: "statusReceiptError", label: "Ошибка обработки заказа" },
] as const;

export function PaymentStatusPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const debug = useDebugEmulations(DEBUG_OPTIONS);
  
  // Определяем статус из эмуляций или из URL параметра
  const status = useMemo(() => {
    // Проверяем debug опции в порядке приоритета
    if (debug.statusSuccess) return "success" as PaymentStatusType;
    if (debug.statusFail) return "fail" as PaymentStatusType;
    if (debug.statusTimeout) return "timeout" as PaymentStatusType;
    if (debug.statusReceiptError) return "receipt_error" as PaymentStatusType;
    // Если эмуляции не выбраны, используем параметр из URL
    return (searchParams.get("status") || "success") as PaymentStatusType;
  }, [debug.statusSuccess, debug.statusFail, debug.statusTimeout, debug.statusReceiptError, searchParams]);

  // Обновляем данные заказов при успешной оплате
  useEffect(() => {
    if (status === "success") {
      void OrderService.fetchOrderHistory();
    }
  }, [status]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (status === "success") {
        prefetchAndNavigate(navigate, "/map", { replace: true });
      } else {
        const machineId = searchParams.get("machineId");
        if (machineId) {
          prefetchAndNavigate(navigate, `/payment?machineId=${machineId}`, { replace: true });
        } else {
          prefetchAndNavigate(navigate, "/cart", { replace: true });
        }
      }
    }, REDIRECT_DELAY);

    return () => clearTimeout(timeout);
  }, [navigate, status, searchParams]);

  const isSuccess = status === "success";

  const getStatusInfo = () => {
    switch (status) {
      case "success":
        return {
          image: "/assets/order/success.png",
          title: "Оплата прошла успешно!",
          description: "А мы уже начали готовить твой заказ"
        };
      case "timeout":
        return {
          image: "/assets/order/fail.png",
          title: "Превышено время ожидания",
          description: "Создание заказа заняло слишком много времени. Попробуйте ещё раз через несколько минут."
        };
      case "receipt_error":
        return {
          image: "/assets/order/fail.png",
          title: "Ошибка обработки заказа",
          description: "Произошла ошибка при формировании заказа. Обратитесь в службу поддержки."
        };
      default:
        return {
          image: "/assets/order/fail.png",
          title: "Оплата не прошла!",
          description: "Попробуйте изменить способ оплаты или добавить новый"
        };
    }
  };

  const statusInfo = getStatusInfo();

  return (
    <Page>
      <Page.SafeArea />
      <Page.DisplayZone>
        <Page.DisplayZone.Body className={styles.body}>
          <div className={styles.content}>
            <div className={styles.imageContainer}>
              <img 
                src={statusInfo.image} 
                alt=""
                className={styles.image}
              />
            </div>
            <div className={styles.textContent}>
              <h2 className={styles.title}>
                {statusInfo.title}
              </h2>
              <p className={styles.description}>
                {statusInfo.description}
              </p>
            </div>
          </div>
        </Page.DisplayZone.Body>
      </Page.DisplayZone>
    </Page>
  );
}
